import { TOKEN, USER } from '@Constants/cookies';
import { loginRequest } from '@Services/auth/auth.req';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { AuthState, LoginPayload } from './authSlice.types';
import { LoginResponse } from '@Services/auth/auth.res.types';

/* side effects */
export const login = createAsyncThunk<
	LoginResponse,
	LoginPayload,
	{ rejectValue: string }
>('auth/login', async ({ email, password }, thunkApi) => {
	try {
		const { data } = await loginRequest(email, password);
		return data;
	} catch (error: any) {
		const msg = error.response?.data.message || 'Login failed';
		return thunkApi.rejectWithValue(msg);
	}
});

/* slice */
/**
 * Auth must be restored synchronously at store creation: loginCheck()
 * is dispatched from an App effect that runs AFTER the first render
 * commit, by which point PrivateRoute has already seen isAuth=false and
 * redirected away — destroying deep links on every refresh.
 */
const readStoredAuth = (): Pick<
	AuthState,
	'isAuth' | 'user' | 'permissions'
> => {
	const permissions = new Map<string, string>();
	try {
		const isAuth = !!localStorage.getItem(TOKEN);
		const prevUser = localStorage.getItem(USER);
		const user = prevUser ? JSON.parse(prevUser) : null;
		if (user)
			for (let i = 0; i < user.permissions?.length; i++)
				permissions.set(user.permissions[i].code, user.permissions[i].name);
		return { isAuth, user, permissions };
	} catch {
		return { isAuth: false, user: null, permissions };
	}
};

const initialState: AuthState = {
	...readStoredAuth(),
	loaders: {
		login: null,
	},
	errors: {
		login: '',
	},
};
const authSlice = createSlice({
	name: 'auth',
	initialState,
	reducers: {
		loginCheck(state) {
			const restored = readStoredAuth();
			state.isAuth = restored.isAuth;
			state.user = restored.user;
			state.permissions = restored.permissions;
		},

		logout(state) {
			state.permissions = new Map();
			state.isAuth = false;
			localStorage.clear();
			// Cookies.remove(TOKEN);
			// Cookies.remove(USER);
		},
	},
	extraReducers: (builder) => {
		/* login builder */
		builder
			.addCase(login.pending, (state) => {
				state.loaders.login = true;
				state.errors.login = '';
			})
			.addCase(login.fulfilled, (state, { payload }) => {
				// Cookies.set(TOKEN, payload.token);
				// Cookies.set(USER, payload.admin);
				localStorage.setItem(USER, JSON.stringify(payload.admin));
				localStorage.setItem(TOKEN, payload.token);
				state.isAuth = true;
				state.user = payload.admin;
				state.loaders.login = false;
				// Fresh map per login: adding only kept a previous account's codes.
				state.permissions = new Map();
				for (let i = 0; i < state.user.permissions.length; i++) {
					state.permissions.set(
						state.user.permissions[i].code,
						state.user.permissions[i].name
					);
				}
			})
			.addCase(login.rejected, (state, action) => {
				state.errors.login = action.payload;
				state.loaders.login = undefined;
			});
	},
});

export default authSlice.reducer;
export const { loginCheck, logout } = authSlice.actions;
